import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, Award, TrendingUp, Play, UserCheck, UserX, Users as UsersIcon, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentHome = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    examsAvailable: 0,
    examsTaken: 0,
    averageScore: 0
  });
  const [myStats, setMyStats] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsRes, submissionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/student/exams`),
        axios.get(`${API}/student/submissions`),
        axios.get(`${API}/student/my-stats`)
      ]);
      
      const submissions = submissionsRes.data;
      const avgScore = submissions.length > 0
        ? submissions.reduce((sum, sub) => sum + sub.score, 0) / submissions.length
        : 0;

      setStats({
        examsAvailable: examsRes.data.length,
        examsTaken: submissions.length,
        averageScore: avgScore
      });
      
      setMyStats(statsRes.data);
      setRecentSubmissions(submissions.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('availableExams'),
      value: stats.examsAvailable,
      icon: ClipboardList,
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-50'
    },
    {
      title: t('myExams'),
      value: stats.examsTaken,
      icon: Award,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50'
    },
    {
      title: t('score'),
      value: `${stats.averageScore.toFixed(1)}%`,
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50'
    }
  ];

  return (
    <div className="space-y-8" data-testid="student-home">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('dashboard')}</h2>
        <p className="text-gray-600">{t('statistics')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover border-0 shadow-lg overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 bg-gradient-to-br ${stat.color} bg-clip-text text-transparent`} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* My Statistics */}
      {myStats && (myStats.participation_rate > 0 || myStats.attendance_rate > 0) && (
        <Card className="shadow-lg border-0 bg-gradient-to-br from-indigo-50 to-cyan-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('myStats')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Participation Rate */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-medium text-gray-700">{t('participationRate')}</span>
                </div>
                <div className="text-3xl font-bold text-indigo-600">
                  {myStats.participation_rate}%
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all"
                    style={{ width: `${myStats.participation_rate}%` }}
                  ></div>
                </div>
              </div>

              {/* Attendance Rate */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">{t('attendanceRate')}</span>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {myStats.attendance_rate}%
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all"
                    style={{ width: `${myStats.attendance_rate}%` }}
                  ></div>
                </div>
              </div>

              {/* Absence Rate */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserX className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-700">{t('absenceRate')}</span>
                </div>
                <div className="text-3xl font-bold text-red-600">
                  {myStats.absence_rate}%
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all"
                    style={{ width: `${myStats.absence_rate}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            {myStats.updated_at && (
              <div className="flex items-center gap-2 text-xs text-gray-600 mt-4 pt-4 border-t border-gray-200">
                <Calendar className="h-3 w-3" />
                {t('lastUpdated')}: {new Date(myStats.updated_at).toLocaleDateString()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>{t('availableExams')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Link to="/student/exams">
            <Button data-testid="view-available-exams" className="w-full bg-gradient-to-r from-cyan-600 to-indigo-600">
              <Play className="h-5 w-5 mr-2" />
              {t('availableExams')}
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Recent Submissions */}
      {recentSubmissions.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle>{t('myResults')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentSubmissions.map((submission, index) => (
                <div
                  key={submission.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">Exam {index + 1}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      submission.score >= 70 ? 'text-green-600' :
                      submission.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {submission.score}%
                    </div>
                    <Link to={`/student/results/${submission.id}`}>
                      <Button data-testid={`view-result-${index}`} variant="link" size="sm">
                        {t('viewDetails')}
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StudentHome;